import { useState } from 'react'
import { LABEL_RE } from './core/runLog'
import { INJECT_DEFAULTS } from './core/injector'

// The debug screen: opened by a long press in the top-left corner, fully
// unmounted (not hidden) before a run starts recording.
export default function DebugScreen({ build, deviceInfo, crash, startup, lastResult, logs, defaultLabel, onStart, onExport, onClose, exportStatus, scene, onSceneAction, sceneStatus }) {
    const [label, setLabel] = useState(defaultLabel)
    const [sustained, setSustained] = useState(false)
    const [minutes, setMinutes] = useState(10)
    const [flash, setFlash] = useState(false)
    const [injectType, setInjectType] = useState('none')
    const [inject, setInject] = useState(INJECT_DEFAULTS)
    const setParam = (type, key) => e => setInject({ ...inject, [type]: { ...inject[type], [key]: Math.max(1, Number(e.target.value) || 1) } })
    const labelOk = LABEL_RE.test(label)
    const d = deviceInfo?.device ?? {}
    const n = deviceInfo?.native ?? {}

    const start = () => {
        if (!labelOk) return
        onStart({
            label,
            mode: sustained ? 'sustained' : 'single',
            durationMs: sustained ? Math.max(0.1, Number(minutes) || 10) * 60_000 : null,
            flash,
            inject: injectType === 'none' ? null : { type: injectType, ...inject[injectType] },
        })
    }

    return (
        <div className="gate1-debug" data-gate1-debug="">
            <div className="gate1-debug__bar">
                <strong>Gate 1 debug</strong>
                <button onClick={onClose}>Close</button>
            </div>

            <section>
                <h3>Run</h3>
                <label>Label <input value={label} onChange={e => setLabel(e.target.value.trim())} autoCapitalize="off" autoCorrect="off" spellCheck={false} /></label>
                {!labelOk && <div className="gate1-debug__warn">Letters, digits, . _ - only (max 48)</div>}
                <label><input type="checkbox" checked={sustained} onChange={e => setSustained(e.target.checked)} /> Sustained loop</label>
                {sustained && <label>Minutes <input type="number" min="0.1" step="0.5" value={minutes} onChange={e => setMinutes(e.target.value)} /></label>}
                <label><input type="checkbox" checked={flash} onChange={e => setFlash(e.target.checked)} /> Video sync flash (dry run only)</label>
                <label>Hold injection (test only, never graded){' '}
                    <select value={injectType} onChange={e => setInjectType(e.target.value)}>
                        <option value="none">none</option>
                        <option value="stall">stall: main-thread busy-wait</option>
                        <option value="gpu">gpu: full-screen overdraw</option>
                    </select>
                </label>
                {injectType === 'stall' && <div className="gate1-debug__row">
                    <label>every <input type="number" min="2" value={inject.stall.every} onChange={setParam('stall', 'every')} /> frames</label>
                    <label>stall <input type="number" min="1" value={inject.stall.stallMs} onChange={setParam('stall', 'stallMs')} /> ms</label>
                </div>}
                {injectType === 'gpu' && <div className="gate1-debug__row">
                    <label>every <input type="number" min="2" value={inject.gpu.every} onChange={setParam('gpu', 'every')} /> frames</label>
                    <label>for <input type="number" min="1" value={inject.gpu.burstFrames} onChange={setParam('gpu', 'burstFrames')} /> frames</label>
                    <label>layers <input type="number" min="1" value={inject.gpu.layers} onChange={setParam('gpu', 'layers')} /></label>
                </div>}
                {injectType !== 'none' && <div className="gate1-debug__warn">Hold injection is on: this run is a logger test, not a graded run.</div>}
                <button className="gate1-debug__start" disabled={!labelOk} onClick={start}>Start run (begins 2 s after this screen closes)</button>
                {lastResult && <pre>{lastResult}</pre>}
            </section>

            <section>
                <h3>War scene and beat</h3>
                <label>Showing{' '}
                    <select value={scene?.current ?? ''} onChange={e => onSceneAction('show', e.target.value)}>
                        {!scene?.current && <option value="">placeholder</option>}
                        {(scene?.names ?? []).map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                </label>
                <label>Beat{' '}
                    <select value={scene?.beat ?? 'reveal'} onChange={e => onSceneAction('beat', e.target.value)}>
                        <option value="reveal">reveal (item C)</option>
                        <option value="placeholder">placeholder</option>
                    </select>
                </label>
                <button onClick={() => onSceneAction('verify')}>Verify fixtures (criterion #8)</button>{' '}
                <button onClick={() => onSceneAction('screenshot')}>Save scene screenshot</button>{' '}
                <button onClick={() => onSceneAction('contextLoss')}>Test: lose WebGL context</button>
                {sceneStatus && <pre>{sceneStatus}</pre>}
            </section>

            <section>
                <h3>Crash / reload counter: {crash?.counts?.total ?? '?'}</h3>
                <pre>{crash ? Object.entries(crash.counts).filter(([k]) => k !== 'total').map(([k, v]) => `${k}: ${v}`).join('\n') : 'loading'}</pre>
                <details>
                    <summary>Event log ({crash?.events?.length ?? 0})</summary>
                    <pre>{(crash?.events ?? []).map(e => `${new Date(e.at).toISOString()}  ${e.counted ? 'COUNTED' : 'logged '}  ${e.type}  run=${e.runLabel ?? '-'}  ${e.detail ? JSON.stringify(e.detail) : ''}`).join('\n') || 'none'}</pre>
                </details>
            </section>

            <section>
                <h3>Build</h3>
                <pre>{[
                    `commit     ${build.commit}`,
                    `build      ${build.buildType} (NODE_ENV=${build.nodeEnv})`,
                    `native     ${n.nativeBuildType ?? '-'} commit=${n.nativeCommit ?? '-'}`,
                    `debuggable ${n.debuggable ?? '-'}  profileable(shell) ${n.profileableByShell ?? '-'}  debugger attached ${n.debuggerAttached ?? '-'}`,
                    `app        ${n.appId ?? '-'}`,
                ].join('\n')}</pre>
                <h3>Device</h3>
                <pre>{[
                    `${d.manufacturer ?? ''} ${d.model ?? ''}  ${d.operatingSystem ?? ''} ${d.osVersion ?? ''}`,
                    `webview    ${d.webViewVersion ?? '-'} ${d.webViewPackage ?? ''}`,
                    `display    ${d.displayRefreshHz ?? '-'} Hz  (modes ${(d.displaySupportedHz ?? []).join(', ') || '-'})`,
                    `screen     ${window.screen.width}×${window.screen.height} @${window.devicePixelRatio}x`,
                ].join('\n')}</pre>
            </section>

            <section>
                <h3>Startup (this launch)</h3>
                <pre>{startup ? [
                    ...Object.entries(startup.sinceProcessStartMs).map(([k, v]) => `${k.padEnd(20)} +${v} ms`),
                    `auth ${startup.authSource ?? '-'}   doc ${startup.doc?.source ?? '-'} (exists ${String(startup.doc?.exists)})`,
                    ...startup.orderProblems.map(p => `ORDER: ${p}`),
                    ...startup.errors.map(e => `ERROR ${e.step}: ${e.code ?? ''} ${e.message}`),
                ].join('\n') : 'pending'}</pre>
            </section>

            <section>
                <h3>Logs ({logs.length})</h3>
                <button onClick={onExport}>Export all logs (share sheet)</button>
                {exportStatus && <div>{exportStatus}</div>}
                <pre>{logs.slice(0, 30).map(f => `${f.name}  ${f.size != null ? Math.round(f.size / 1024) + ' KB' : ''}`).join('\n')}</pre>
            </section>
        </div>
    )
}
